import React, { useEffect, useMemo, useState } from 'react';
import {
  FileText, CheckCircle, XCircle, ArrowClockwise, CircleNotch, Receipt,
  MagnifyingGlass, X, Download, Package,
} from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';

const API = `${window.location.origin}/api`;

const STATUS_TABS = [
  { value: 'pending',  label: 'Do przejrzenia', color: 'amber' },
  { value: 'imported', label: 'Zaimportowane',  color: 'green' },
  { value: 'rejected', label: 'Odrzucone',      color: 'slate' },
];

const MONTHS_PL = [
  'styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec',
  'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień',
];

const fmtPLN = (n) =>
  n == null ? '—' : Number(n).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' zł';

export default function KsefTab() {
  const now = new Date();
  const [status, setStatus] = useState('pending');
  const [year, setYear] = useState(0);   // 0 = wszystkie
  const [month, setMonth] = useState(0); // 0 = wszystkie
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, imported: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [pulling, setPulling] = useState(false);
  const [rowBusy, setRowBusy] = useState({}); // id → 'import'|'reject'|'download'
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = { status, limit: 500 };
      if (year && month) { params.year = year; params.month = month; }
      else if (year) { params.year = year; }
      if (q.trim()) params.q = q.trim();
      const { data } = await axios.get(`${API}/ksef/invoices`, { params });
      setRows(data.invoices || []);
      setCounts(data.counts || { pending: 0, imported: 0, rejected: 0 });
    } catch (e) {
      console.error('KSeF load failed', e);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search: reload when q changes
  useEffect(() => {
    const t = setTimeout(() => load(), q ? 400 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, year, month, q]);

  const pullNow = async () => {
    setPulling(true);
    try {
      const { data } = await axios.post(`${API}/ksef/pull`);
      if (data.ok) {
        alert(`Pobrano: +${data.inserted} nowych, pominięto ${data.skipped}`);
        setStatus('pending');
        await load();
      } else {
        alert(`Błąd: ${data.error}`);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd pobrania');
    } finally { setPulling(false); }
  };

  const handleImport = async (id) => {
    setRowBusy((p) => ({ ...p, [id]: 'import' }));
    try {
      const { data } = await axios.post(`${API}/ksef/invoices/${id}/import`);
      if (data.xml_error) {
        alert(`Zaimportowano (bez XML: ${data.xml_error}). Klik ponowny za godzinę doda XML.`);
      }
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd importu');
    } finally { setRowBusy((p) => { const q2 = { ...p }; delete q2[id]; return q2; }); }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Odrzucić tę fakturę? Nie trafi do Kosztów.')) return;
    setRowBusy((p) => ({ ...p, [id]: 'reject' }));
    try {
      await axios.post(`${API}/ksef/invoices/${id}/reject`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd');
    } finally { setRowBusy((p) => { const q2 = { ...p }; delete q2[id]; return q2; }); }
  };

  const handleDownload = async (row) => {
    setRowBusy((p) => ({ ...p, [row.id]: 'download' }));
    try {
      const resp = await axios.get(`${API}/ksef/invoices/${row.id}/download`, {
        params: { fmt: 'pdf' },
        responseType: 'blob',
        timeout: 60000,
      });
      const disp = resp.headers['content-disposition'] || '';
      const m = disp.match(/filename\*=UTF-8''([^;]+)|filename="?([^;"]+)"?/i);
      const filename = decodeURIComponent(m?.[1] || m?.[2] || `KSeF_${row.ksef_number}.pdf`);
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd pobierania faktury');
    } finally { setRowBusy((p) => { const q2 = { ...p }; delete q2[row.id]; return q2; }); }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const bulkImport = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Dodać ${selected.size} faktur do Kosztów firmy? Każda trafi do miesiąca zgodnego z datą wystawienia.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/ksef/invoices/import-bulk`, {
        invoice_ids: [...selected],
      });
      const groups = {};
      for (const item of (data.imported || [])) {
        if (item.year && item.month) {
          const key = `${item.year}/${String(item.month).padStart(2, '0')}`;
          groups[key] = (groups[key] || 0) + 1;
        }
      }
      const summary = Object.entries(groups)
        .map(([k, v]) => `${k}: ${v}`).join(', ');
      const errCnt = (data.errors || []).length;
      alert(`Zaimportowano ${data.imported.length}/${data.total}${summary ? ` (${summary})` : ''}${errCnt ? `. Błędów: ${errCnt}` : ''}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd importu masowego');
    } finally { setBulkBusy(false); }
  };

  const clearFilters = () => {
    setYear(0); setMonth(0); setQ('');
  };

  const grossTotal = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.gross_amount) || 0), 0),
    [rows]
  );
  const selectedGross = useMemo(
    () => rows.filter((r) => selected.has(r.id)).reduce((s, r) => s + (Number(r.gross_amount) || 0), 0),
    [rows, selected]
  );

  const years = useMemo(() => {
    const currentYear = now.getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-[1920px] mx-auto px-6 py-6 space-y-4" data-testid="ksef-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Receipt className="w-8 h-8 text-blue-600" weight="bold" /> KSeF — faktury zakupowe
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Pobrane z Krajowego Systemu e-Faktur. Zaznacz i dodaj do Kosztów firmy — trafią do miesiąca zgodnego z datą wystawienia.
          </p>
        </div>
        <button
          onClick={pullNow}
          disabled={pulling}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-sm disabled:opacity-50"
          data-testid="ksef-pull-now-btn"
        >
          {pulling ? <CircleNotch className="w-4 h-4 animate-spin" /> : <ArrowClockwise className="w-4 h-4" weight="bold" />}
          Pobierz teraz
        </button>
      </div>

      {/* Status tabs + filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          {STATUS_TABS.map((t) => {
            const active = status === t.value;
            const n = counts[t.value] ?? 0;
            const bg = t.color === 'amber' ? 'bg-amber-500'
                     : t.color === 'green' ? 'bg-green-600'
                                           : 'bg-slate-700';
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setStatus(t.value)}
                data-testid={`ksef-status-tab-${t.value}`}
                className={`px-4 py-1.5 rounded-md text-xs font-semibold transition ${active ? `${bg} text-white shadow-sm` : 'text-slate-600 hover:bg-slate-100'}`}
              >
                {t.label}
                <span className={`ml-1.5 inline-flex items-center justify-center min-w-[20px] h-4 px-1 rounded text-[9px] font-bold ${active ? 'bg-white/25' : 'bg-slate-200 text-slate-600'}`}>{n}</span>
              </button>
            );
          })}
        </div>

        <select
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value) || 0)}
          className="px-3 py-1.5 border border-slate-300 rounded-md text-xs"
          data-testid="ksef-year-filter"
        >
          <option value={0}>Wszystkie lata</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select
          value={month}
          onChange={(e) => setMonth(parseInt(e.target.value) || 0)}
          className="px-3 py-1.5 border border-slate-300 rounded-md text-xs"
          data-testid="ksef-month-filter"
        >
          <option value={0}>Wszystkie miesiące</option>
          {MONTHS_PL.map((m, i) => <option key={i + 1} value={i + 1}>{i + 1} — {m}</option>)}
        </select>

        <div className="relative">
          <MagnifyingGlass className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Szukaj: NIP, nazwa, nr faktury..."
            className="pl-8 pr-8 py-1.5 border border-slate-300 rounded-md text-xs min-w-[280px]"
            data-testid="ksef-search-input"
          />
          {q && (
            <button
              onClick={() => setQ('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              title="Wyczyść"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {(year > 0 || month > 0 || q) && (
          <button
            onClick={clearFilters}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
            data-testid="ksef-clear-filters"
          >
            Wyczyść filtry
          </button>
        )}
      </div>

      {/* Bulk actions */}
      {status === 'pending' && selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
          <div className="text-sm text-blue-900 font-semibold">
            Zaznaczono {selected.size} faktur · Suma brutto: {fmtPLN(selectedGross)}
          </div>
          <div className="flex-1" />
          <button
            onClick={bulkImport}
            disabled={bulkBusy}
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-xs font-semibold shadow-sm disabled:opacity-50"
            data-testid="ksef-bulk-import-btn"
          >
            {bulkBusy ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" weight="bold" />}
            Dodaj do Kosztów
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-slate-600 hover:text-slate-900 underline"
          >
            Odznacz
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <CircleNotch className="w-8 h-8 text-slate-400 animate-spin mx-auto" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-lg">
              {q || year || month ? 'Brak faktur w wybranych filtrach' :
                (status === 'pending' ? 'Brak faktur do przejrzenia' : `Brak faktur (${status})`)}
            </p>
            {status === 'pending' && !q && !year && !month && (
              <p className="text-slate-400 text-sm mt-1">Kliknij "Pobierz teraz" aby ściągnąć z KSeF</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-xs uppercase text-slate-600">
                <tr>
                  {status === 'pending' && (
                    <th className="py-3 px-3 w-8">
                      <input
                        type="checkbox"
                        checked={selected.size > 0 && selected.size === rows.length}
                        onChange={toggleAll}
                        className="w-4 h-4"
                        data-testid="ksef-select-all"
                        title={selected.size === rows.length ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                      />
                    </th>
                  )}
                  <th className="py-3 px-4 text-left font-semibold">Data</th>
                  <th className="py-3 px-4 text-left font-semibold">Numer faktury</th>
                  <th className="py-3 px-4 text-left font-semibold">Sprzedawca</th>
                  <th className="py-3 px-4 text-left font-semibold">NIP</th>
                  <th className="py-3 px-4 text-right font-semibold">Brutto</th>
                  <th className="py-3 px-4 text-center font-semibold">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isSel = selected.has(r.id);
                  return (
                    <tr
                      key={r.id}
                      className={`border-t border-slate-100 ${isSel ? 'bg-blue-50' : 'hover:bg-blue-50/40'}`}
                      data-testid={`ksef-row-${r.id}`}
                    >
                      {status === 'pending' && (
                        <td className="py-3 px-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleOne(r.id)}
                            className="w-4 h-4"
                            data-testid={`ksef-select-${r.id}`}
                          />
                        </td>
                      )}
                      <td className="py-3 px-4 font-mono text-slate-700">{r.issue_date || '—'}</td>
                      <td className="py-3 px-4 font-semibold text-slate-800">{r.invoice_number || '—'}</td>
                      <td className="py-3 px-4 text-slate-700 truncate max-w-xs" title={r.seller_name}>
                        {r.seller_name || '—'}
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-slate-500">{r.seller_nip || '—'}</td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-slate-800 tabular-nums">
                        {fmtPLN(r.gross_amount)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => handleDownload(r)}
                            disabled={!!rowBusy[r.id]}
                            className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded text-[11px] font-semibold disabled:opacity-50"
                            data-testid={`ksef-download-${r.id}`}
                            title="Pobierz PDF (lub XML jeśli PDF niedostępny)"
                          >
                            {rowBusy[r.id] === 'download'
                              ? <CircleNotch className="w-3 h-3 animate-spin" />
                              : <Download className="w-3 h-3" weight="bold" />}
                            PDF
                          </button>
                          {status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleImport(r.id)}
                                disabled={!!rowBusy[r.id]}
                                className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-[11px] font-semibold disabled:opacity-50"
                                data-testid={`ksef-import-${r.id}`}
                                title="Importuj do Kosztów firmy"
                              >
                                {rowBusy[r.id] === 'import'
                                  ? <CircleNotch className="w-3 h-3 animate-spin" />
                                  : <CheckCircle className="w-3 h-3" weight="bold" />}
                                Importuj
                              </button>
                              <button
                                onClick={() => handleReject(r.id)}
                                disabled={!!rowBusy[r.id]}
                                className="inline-flex items-center gap-1 bg-slate-200 hover:bg-slate-300 text-slate-700 px-2 py-1 rounded text-[11px] font-semibold disabled:opacity-50"
                                data-testid={`ksef-reject-${r.id}`}
                              >
                                {rowBusy[r.id] === 'reject'
                                  ? <CircleNotch className="w-3 h-3 animate-spin" />
                                  : <XCircle className="w-3 h-3" weight="bold" />}
                                Odrzuć
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                <tr>
                  <td colSpan={status === 'pending' ? 5 : 4} className="py-2 px-4 text-right text-xs font-semibold text-slate-600 uppercase">
                    Razem ({rows.length} faktur)
                  </td>
                  <td className="py-2 px-4 text-right font-mono font-bold text-slate-800 tabular-nums">
                    {fmtPLN(grossTotal)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
